import { schema } from 'normalizr';
import { createActions, createReducer } from 'reduxsauce';

import {
  entityFetching,
  entityFetchingFailure,
  entityFetchingSuccess,
} from '../../utils/redux';

export const couponsEntity = new schema.Entity('coupons');

export const INITIAL_STATE = {
  entities: {
    [couponsEntity.key]: {},
  },
  result: {
    [couponsEntity.key]: {
      fetching: false,
      fetched: false,
      results: [],
      error: null,
    },
    websiteCityCoupons: {
      fetching: false,
      fetched: false,
      results: [],
      error: null,
      next: null,
    },
  },
  couponsActions: {
    fetching: false,
    fetched: false,
    data: {},
    error: null,
    couponId: null,
  },
  viewCounts: {},
  affiliateCoupons: {
  fetching: false,
  fetched: false,
  error: null,
  // keyed by business id: { [businessId]: { fetching, fetched, data, error } }
  byBusiness: {},
},
  // Newest published coupons across the whole system (admin analytics sidebar).
  // `data` is a plain array of coupons, newest first.
  newestCoupons: {
    fetching: false,
    fetched: false,
    error: null,
    data: [],
  },
};

const { Types, Creators } = createActions(
  {
    getBusinessCoupons: [],
    getBusinessCouponsRequest: [],
    getBusinessCouponsSuccess: ['data'],
    getBusinessCouponsFailure: ['error'],

    getBusinessCouponsNextPage: [],
    getBusinessCouponsNextPageRequest: [],
    getBusinessCouponsNextPageSuccess: ['data'],
    getBusinessCouponsNextPageFailure: ['error'],
    getBusinessCouponsNextPageNotFound: [],

    getWebsiteCityCoupons: ['params'],
    getWebsiteCityCouponsRequest: [],
    getWebsiteCityCouponsSuccess: ['data'],
    getWebsiteCityCouponsFailure: ['error'],

    getWebsiteCityCouponsNextPage: ['params'],
    getWebsiteCityCouponsNextPageRequest: [],
    getWebsiteCityCouponsNextPageSuccess: ['data'],
    getWebsiteCityCouponsNextPageFailure: ['error'],
    getWebsiteCityCouponsNextPageNotFound: [],

    publishCoupon: ['id', 'resolve'],
    publishCouponRequest: ['id'],
    publishCouponSuccess: ['data'],
    publishCouponFailure: ['error'],

    deleteCoupon: ['id', 'resolve'],
    deleteCouponRequest: ['id'],
    deleteCouponSuccess: ['id'],
    deleteCouponFailure: ['error'],

    sponsorCoupon: ['id', 'resolve'],
    sponsorCouponRequest: ['id'],
    sponsorCouponSuccess: ['data'],
    sponsorCouponFailure: ['error'],

    republishCoupon: ['data', 'resolve'],
    republishCouponRequest: ['id'],
    republishCouponSuccess: ['data'],
    republishCouponFailure: ['error'],

    getViewCounts: ['couponIds'],
    getViewCountsRequest: [],
    getViewCountsSuccess: ['data'],
    getViewCountsFailure: ['error'],

    fetchAllAffiliateCoupons: [],
    fetchAllAffiliateCouponsRequest: [],
    fetchAllAffiliateCouponsBusinessRequest: ['businessId'],
    fetchAllAffiliateCouponsBusinessSuccess: ['businessId', 'data'],
    fetchAllAffiliateCouponsBusinessFailure: ['businessId', 'error'],
    fetchAllAffiliateCouponsComplete: [],

    getNewestCoupons: ['params'],
    getNewestCouponsRequest: [],
    getNewestCouponsSuccess: ['data'],
    getNewestCouponsFailure: ['error'],
  },
  { prefix: 'cb/coupons/' },
);

export { Types as types, Creators as actions };

export const request = (state, action) => {
  const { id } = action;
  return {
    ...state,
    couponsActions: {
      ...state.couponsActions,
      fetching: true,
      couponId: id,
    },
  };
};

const updateEntityItemSuccess = (state, action) => {
  const { data } = action;
  const stateToUpdate = {
    ...state,
    couponsActions: {
      ...state.couponsActions,
      fetched: true,
      fetching: false,
      error: null,
      data,
    },
  };
  stateToUpdate.entities[couponsEntity.key][data.id] = data;
  return stateToUpdate;
};

export const republishSuccess = (state, action) => {
  const { data } = action;

  const updatedState = updateEntityItemSuccess(state, action);
  const updatedResults = updatedState.result[couponsEntity.key].results;

  updatedResults.unshift(data.id);

  const prevCouponIndex = updatedResults.indexOf(state.couponsActions.couponId);

  if (~prevCouponIndex) {
    updatedResults.splice(prevCouponIndex, 1);
  }

  return updatedState;
};

export const deleteSuccess = (state, action) => {
  const entitiesToUpdate = { ...state.entities[couponsEntity.key] };
  delete entitiesToUpdate[action.id];

  const updatedBusinessResults = state.result[couponsEntity.key].results.filter(
    value => value !== action.id,
  );

  const updatedWebsiteCityResults = state.result.websiteCityCoupons.results.filter(
    value => value !== action.id,
  );

  return {
    ...state,
    entities: {
      ...state.entities,
      [couponsEntity.key]: entitiesToUpdate,
    },
    result: {
      ...state.result,
      [couponsEntity.key]: {
        ...state.result[couponsEntity.key],
        results: updatedBusinessResults,
      },
      websiteCityCoupons: {
        ...state.result.websiteCityCoupons,
        results: updatedWebsiteCityResults,
      },
    },
    couponsActions: {
      ...state.couponsActions,
      fetched: true,
      fetching: false,
      error: null,
      data: {},
    },
  };
};

export const failure = (state, action) => {
  const { error } = action;
  return {
    ...state,
    couponsActions: {
      ...state.couponsActions,
      fetching: false,
      error,
    },
  };
};

export const couponNextPageNotFound = state => {
  return {
    ...state,
    result: {
      ...state.result,
      [couponsEntity.key]: {
        ...state.result[couponsEntity.key],
        next: null,
        fetching: false,
        fetched: true,
        error: null,
      },
    },
  };
};

export const websiteCityCouponsRequest = state => ({
  ...state,
  result: {
    ...state.result,
    websiteCityCoupons: {
      ...state.result.websiteCityCoupons,
      fetching: true,
      fetched: false,
      results: [],
      error: null,
      next: null,
    },
  },
});

export const websiteCityCouponsSuccess = (state, { data }) => {
  const coupons = (data && data.results) || [];
  const updatedEntities = { ...state.entities[couponsEntity.key] };

  coupons.forEach(coupon => {
    updatedEntities[coupon.id] = coupon;
  });

  return {
    ...state,
    entities: {
      ...state.entities,
      [couponsEntity.key]: updatedEntities,
    },
    result: {
      ...state.result,
      websiteCityCoupons: {
        ...state.result.websiteCityCoupons,
        fetching: false,
        fetched: true,
        results: coupons.map(coupon => coupon.id),
        error: null,
        next: (data && data.next) || null,
      },
    },
  };
};

export const websiteCityCouponsFailure = (state, { error }) => ({
  ...state,
  result: {
    ...state.result,
    websiteCityCoupons: {
      ...state.result.websiteCityCoupons,
      fetching: false,
      fetched: true,
      error,
    },
  },
});

export const websiteCityCouponsNextPageRequest = state => ({
  ...state,
  result: {
    ...state.result,
    websiteCityCoupons: {
      ...state.result.websiteCityCoupons,
      fetching: true,
      error: null,
    },
  },
});

export const websiteCityCouponsNextPageSuccess = (state, { data }) => {
  const coupons = (data && data.results) || [];
  const updatedEntities = { ...state.entities[couponsEntity.key] };

  coupons.forEach(coupon => {
    updatedEntities[coupon.id] = coupon;
  });

  const existingResults = state.result.websiteCityCoupons.results || [];
  const incomingIds = coupons.map(coupon => coupon.id);

  return {
    ...state,
    entities: {
      ...state.entities,
      [couponsEntity.key]: updatedEntities,
    },
    result: {
      ...state.result,
      websiteCityCoupons: {
        ...state.result.websiteCityCoupons,
        fetching: false,
        fetched: true,
        results: [...existingResults, ...incomingIds],
        error: null,
        next: (data && data.next) || null,
      },
    },
  };
};

export const websiteCityCouponsNextPageNotFound = state => ({
  ...state,
  result: {
    ...state.result,
    websiteCityCoupons: {
      ...state.result.websiteCityCoupons,
      fetching: false,
      fetched: true,
      error: null,
      next: null,
    },
  },
});

export const viewCountsRequest = state => ({
  ...state,
  couponsActions: {
    ...state.couponsActions,
    fetching: true,
    error: null,
  },
});

export const viewCountsSuccess = (state, { data }) => ({
  ...state,
  viewCounts: data,
  couponsActions: {
    ...state.couponsActions,
    fetched: true,
    fetching: false,
    error: null,
  },
});

export const viewCountsFailure = (state, { error }) => ({
  ...state,
  couponsActions: {
    ...state.couponsActions,
    fetching: false,
    error,
  },
});

export const fetchAllAffiliateCouponsRequest = (state) => ({
  ...state,
  affiliateCoupons: {
    ...state.affiliateCoupons,
    fetching: true,
    fetched: false,
    error: null,
  },
});

export const fetchAllAffiliateCouponsBusinessRequest = (state, { businessId }) => ({
  ...state,
  affiliateCoupons: {
    ...state.affiliateCoupons,
    byBusiness: {
      ...state.affiliateCoupons.byBusiness,
      [businessId]: {
        fetching: true,
        fetched: false,
        data: null,
        error: null,
      },
    },
  },
});

export const fetchAllAffiliateCouponsBusinessSuccess = (state, { businessId, data }) => ({
  ...state,
  affiliateCoupons: {
    ...state.affiliateCoupons,
    byBusiness: {
      ...state.affiliateCoupons.byBusiness,
      [businessId]: {
        fetching: false,
        fetched: true,
        data,
        error: null,
      },
    },
  },
});

export const fetchAllAffiliateCouponsBusinessFailure = (state, { businessId, error }) => ({
  ...state,
  affiliateCoupons: {
    ...state.affiliateCoupons,
    byBusiness: {
      ...state.affiliateCoupons.byBusiness,
      [businessId]: {
        fetching: false,
        fetched: false,
        data: null,
        error,
      },
    },
  },
});

export const fetchAllAffiliateCouponsComplete = (state) => ({
  ...state,
  affiliateCoupons: {
    ...state.affiliateCoupons,
    fetching: false,
    fetched: true,
  },
});

export const newestCouponsRequest = state => ({
  ...state,
  newestCoupons: {
    ...state.newestCoupons,
    fetching: true,
    error: null,
  },
});

export const newestCouponsSuccess = (state, { data }) => ({
  ...state,
  newestCoupons: {
    ...state.newestCoupons,
    fetching: false,
    fetched: true,
    error: null,
    data: Array.isArray(data) ? data : (data && data.results) || [],
  },
});

export const newestCouponsFailure = (state, { error }) => ({
  ...state,
  newestCoupons: {
    ...state.newestCoupons,
    fetching: false,
    error,
  },
});

export const HANDLERS = {
  [Types.GET_BUSINESS_COUPONS_REQUEST]: entityFetching(couponsEntity),
  [Types.GET_BUSINESS_COUPONS_SUCCESS]: entityFetchingSuccess(couponsEntity),
  [Types.GET_BUSINESS_COUPONS_FAILURE]: entityFetchingFailure(couponsEntity),

  [Types.GET_BUSINESS_COUPONS_NEXT_PAGE_REQUEST]: entityFetching(couponsEntity),
  [Types.GET_BUSINESS_COUPONS_NEXT_PAGE_SUCCESS]: entityFetchingSuccess(
    couponsEntity,
    { attach: true },
  ),
  [Types.GET_BUSINESS_COUPONS_NEXT_PAGE_NOT_FOUND]: couponNextPageNotFound,
  [Types.GET_BUSINESS_COUPONS_NEXT_PAGE_FAILURE]: entityFetchingFailure(
    couponsEntity,
  ),

  [Types.GET_WEBSITE_CITY_COUPONS_REQUEST]: websiteCityCouponsRequest,
  [Types.GET_WEBSITE_CITY_COUPONS_SUCCESS]: websiteCityCouponsSuccess,
  [Types.GET_WEBSITE_CITY_COUPONS_FAILURE]: websiteCityCouponsFailure,

  [Types.GET_WEBSITE_CITY_COUPONS_NEXT_PAGE_REQUEST]: websiteCityCouponsNextPageRequest,
  [Types.GET_WEBSITE_CITY_COUPONS_NEXT_PAGE_SUCCESS]: websiteCityCouponsNextPageSuccess,
  [Types.GET_WEBSITE_CITY_COUPONS_NEXT_PAGE_FAILURE]: websiteCityCouponsFailure,
  [Types.GET_WEBSITE_CITY_COUPONS_NEXT_PAGE_NOT_FOUND]: websiteCityCouponsNextPageNotFound,

  [Types.PUBLISH_COUPON_REQUEST]: request,
  [Types.PUBLISH_COUPON_SUCCESS]: updateEntityItemSuccess,
  [Types.PUBLISH_COUPON_FAILURE]: failure,

  [Types.DELETE_COUPON_REQUEST]: request,
  [Types.DELETE_COUPON_SUCCESS]: deleteSuccess,
  [Types.DELETE_COUPON_FAILURE]: failure,

  [Types.SPONSOR_COUPON_REQUEST]: request,
  [Types.SPONSOR_COUPON_SUCCESS]: updateEntityItemSuccess,
  [Types.SPONSOR_COUPON_FAILURE]: failure,

  [Types.REPUBLISH_COUPON_REQUEST]: request,
  [Types.REPUBLISH_COUPON_SUCCESS]: republishSuccess,
  [Types.REPUBLISH_COUPON_FAILURE]: failure,

  [Types.GET_VIEW_COUNTS_REQUEST]: viewCountsRequest,
  [Types.GET_VIEW_COUNTS_SUCCESS]: viewCountsSuccess,
  [Types.GET_VIEW_COUNTS_FAILURE]: viewCountsFailure,

  [Types.FETCH_ALL_AFFILIATE_COUPONS_REQUEST]: fetchAllAffiliateCouponsRequest,
[Types.FETCH_ALL_AFFILIATE_COUPONS_BUSINESS_REQUEST]: fetchAllAffiliateCouponsBusinessRequest,
[Types.FETCH_ALL_AFFILIATE_COUPONS_BUSINESS_SUCCESS]: fetchAllAffiliateCouponsBusinessSuccess,
[Types.FETCH_ALL_AFFILIATE_COUPONS_BUSINESS_FAILURE]: fetchAllAffiliateCouponsBusinessFailure,
[Types.FETCH_ALL_AFFILIATE_COUPONS_COMPLETE]: fetchAllAffiliateCouponsComplete,

  [Types.GET_NEWEST_COUPONS_REQUEST]: newestCouponsRequest,
  [Types.GET_NEWEST_COUPONS_SUCCESS]: newestCouponsSuccess,
  [Types.GET_NEWEST_COUPONS_FAILURE]: newestCouponsFailure,
};

export default createReducer(INITIAL_STATE, HANDLERS);